import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { ArrowLeftIcon, GripVertical, Search } from 'lucide-react';
import { useUser } from '@r/api/auth.tsx';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail
} from '@ui/sidebar.tsx';
import { Avatar, AvatarFallback } from '@ui/avatar.tsx';
import { Button } from '@ui/button.tsx';
import { Skeleton } from '@ui/skeleton.tsx';
import { NavUser } from '@r/components/nav-user.tsx';
import { useApiAdminusersGetCollection } from '@r/api/generated/client.ts';
import type { UserUserCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import {
  HIERARCHY_SALE_DRAG_TYPE,
  useHierarchyCanvasStore
} from '@admin/components/hierarchy/hierarchy-canvas-store.ts';
import { WorkspaceClientLink } from '@admin/components/navigation/workspace-client-link.tsx';

/**
 * Couleur d'avatar stable pour un même id : teinte HSL dérivée directement
 * du hash (0-360°), saturation/luminosité fixes — pas de palette à
 * maintenir une infinité de couleurs bien réparties et lisibles.
 */
function getAvatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }
  return `hsl(${Math.abs(hash) % 360}, 65%, 45%)`;
}

/**
 * Sidebar dédiée à la page canvas d'une hiérarchie (`/settings/hierarchy/$id`,
 * cf. `layout.tsx`) : remplace la sidebar standard par la liste des
 * commerciaux disponibles, à glisser-déposer directement sur le canvas.
 * Même requête que celle utilisée historiquement par le canvas (React Query
 * dédup les 2 appels identiques, pas de coût réseau supplémentaire) ; les
 * commerciaux déjà posés sur le canvas sont exclus via `hierarchy-canvas-store`.
 */
export function HierarchySidebar() {
  const { t } = useTranslation();
  const user = useUser();
  const [search, setSearch] = useState('');
  const placedUniqueCodes = useHierarchyCanvasStore((state) => state.placedUniqueCodes);
  const { workspace } = UseWorkspace(user.data);

  const salesQuery = useApiAdminusersGetCollection({
    'maxRole[]': ['ROLE_SALES_ADMIN'],
    perPage: 500,
    'order[firstname]': 'asc'
  });

  const sales = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const users = (salesQuery.data?.data ?? []) as UserUserCollectionReadModel[];
    // Une seule passe plutôt que filter/map/filter/filter enchaînés.
    return users.reduce<{ label: string; uniqueCode: string }[]>((sales, user) => {
      const uniqueCode = user.userWorkspaces?.[0]?.uniqueCode;
      if (!uniqueCode || placedUniqueCodes.has(uniqueCode)) return sales;
      if (!user.fullname.toLowerCase().includes(normalizedSearch)) return sales;
      sales.push({ label: user.fullname, uniqueCode });
      return sales;
    }, []);
  }, [salesQuery.data?.data, placedUniqueCodes, search]);

  function getUserFullName() {
    return `${user.data?.firstname ?? ''} ${user.data?.lastname ?? ''}`.trim();
  }

  return (
    <Sidebar collapsible="icon" className="bg-sidebar">
      <SidebarHeader className="px-3 py-3 space-y-2 border-b border-border">
        <Link
          to="/settings/hierarchy"
          className="inline-flex gap-2 items-center text-sm font-medium text-primary hover:underline"
        >
          <Button rounded="full" size="sm">
            <ArrowLeftIcon />
            {t('common.back')}
          </Button>
        </Link>

        <p className="text-sm font-semibold">{t('admin.hierarchy.sidebar.title')}</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none size-4 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('admin.hierarchy.sidebar.search.placeholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pr-3 pl-9 w-full h-10 text-sm rounded-full border-0 bg-muted text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </SidebarHeader>

      <SidebarContent className="gap-0">
        {salesQuery.isLoading &&
          Array.from({ length: 8 }, (_, index) => (
            <div
              key={index}
              className="flex gap-2 items-center px-3 py-4 border-b border-border last:border-0"
            >
              <Skeleton className="w-6 h-6 rounded-full shrink-0" />
              <Skeleton className="w-32 h-4" />
            </div>
          ))}

        {!salesQuery.isLoading &&
          sales.map((sale) => (
            <button
              key={sale.uniqueCode}
              type="button"
              draggable
              onDragStart={(event) => {
                event.dataTransfer.setData(HIERARCHY_SALE_DRAG_TYPE, sale.uniqueCode);
                event.dataTransfer.effectAllowed = 'move';
              }}
              className="flex gap-2 items-center px-3 py-4 w-full text-sm text-left border-b cursor-grab border-border last:border-0 active:cursor-grabbing"
            >
              <Avatar className="w-6 h-6 shrink-0">
                <AvatarFallback
                  className="text-[0.8rem]"
                  text={sale.label}
                  initials
                  backgroundColor={getAvatarColor(sale.uniqueCode)}
                  color="white"
                />
              </Avatar>
              <span className="truncate">{sale.label}</span>
              <GripVertical className="ml-auto size-3.5 shrink-0 text-muted-foreground" />
            </button>
          ))}

        {sales.length === 0 && !salesQuery.isLoading && (
          <p className="px-3 py-2 text-sm text-muted-foreground">
            {t('admin.hierarchy.sidebar.empty')}
          </p>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-border">
        <WorkspaceClientLink domain={workspace?.domain} />
        <NavUser
          user={{
            name: getUserFullName(),
            email: user.data?.email ?? '',
            avatar: user.data?.avatar ?? null
          }}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
